/**
 * ConfirmModal Component
 *
 * Confirmation dialog before sending a notification campaign.
 * Following the same pattern as the save modal in SegmentsPage.
 */

import { AlertTriangle, Send, X } from "lucide-react";
import { Button } from "../../../shared/components/ui/Button";
import { useFocusTrap } from "../../../hooks/useFocusTrap";

interface ConfirmModalProps {
  recipientCount: number;
  segmentName: string;
  title: string;
  onConfirm: () => void;
  onCancel: () => void;
  sending: boolean;
}

export function ConfirmModal({
  recipientCount,
  segmentName,
  title,
  onConfirm,
  onCancel,
  sending,
}: ConfirmModalProps) {
  const trapRef = useFocusTrap<HTMLDivElement>(true);

  return (
    <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50 animate-fade-in">
      <div
        ref={trapRef}
        role="dialog"
        aria-modal="true"
        aria-labelledby="confirm-modal-title"
        className="bg-white rounded-2xl shadow-2xl max-w-md w-full mx-4 overflow-hidden"
      >
        {/* Header */}
        <div className="px-6 py-4 border-b border-slate-100 flex items-center justify-between">
          <h3 id="confirm-modal-title" className="font-semibold text-lg text-slate-900">
            Confirmar Envío
          </h3>
          <button
            onClick={onCancel}
            className="w-8 h-8 rounded-lg flex items-center justify-center text-slate-400 hover:text-slate-600 hover:bg-slate-100 transition-all"
          >
            <X size={18} />
          </button>
        </div>

        {/* Body */}
        <div className="p-6">
          <div className="flex items-start gap-4 mb-5">
            <div className="w-12 h-12 bg-amber-100 rounded-xl flex items-center justify-center flex-shrink-0">
              <AlertTriangle size={22} className="text-amber-600" />
            </div>
            <div>
              <p className="text-slate-900 font-medium mb-1">
                Estás a punto de enviar una notificación
              </p>
              <p className="text-slate-500 text-sm">
                Esta acción no se puede deshacer. La notificación llegará a los
                dispositivos de los usuarios inmediatamente.
              </p>
            </div>
          </div>

          {/* Summary */}
          <div className="bg-slate-50 rounded-xl p-4 space-y-2.5">
            <div className="flex items-center justify-between text-sm">
              <span className="text-slate-500">Segmento</span>
              <span className="font-semibold text-violet-600">
                {segmentName}
              </span>
            </div>
            <div className="flex items-center justify-between text-sm">
              <span className="text-slate-500">Destinatarios</span>
              <span className="font-bold text-slate-900">
                {recipientCount.toLocaleString()} personas
              </span>
            </div>
            <div className="flex items-center justify-between text-sm">
              <span className="text-slate-500">Título</span>
              <span className="font-medium text-slate-700 truncate max-w-[200px]">
                {title}
              </span>
            </div>
          </div>
        </div>

        {/* Footer */}
        <div className="flex gap-3 px-6 py-4 bg-slate-50 border-t border-slate-100">
          <Button
            variant="secondary"
            onClick={onCancel}
            disabled={sending}
            className="flex-1"
          >
            Cancelar
          </Button>
          <button
            onClick={onConfirm}
            disabled={sending}
            className={`
                            flex-1 py-2.5 rounded-xl font-semibold text-sm flex items-center justify-center gap-2 transition-all duration-300
                            ${
                              sending
                                ? "bg-violet-400 cursor-not-allowed"
                                : "bg-violet-600 hover:bg-violet-700 active:scale-[0.98] shadow-lg shadow-violet-500/25"
                            }
                            text-white
                        `}
          >
            <Send size={15} />
            {sending ? "Enviando..." : "Confirmar Envío"}
          </button>
        </div>
      </div>
    </div>
  );
}

export default ConfirmModal;
